import { IN_STOCK } from '@jouzen/ecom-components/v3/consts/stockStatus';
import type { RingSizeData } from '@jouzen/ecom-components/v3/types/ringSizeData';
import type { StockStatus } from '@jouzen/ecom-components/v3/types/stockStatus';
import { useMemo } from 'react';

import type { CurrentRouteValues } from '@/app/[locale]/store/_components/HardwarePDP/_hooks/useCurrentRoute';
import type { ListProduct } from '@/queries/utils/mapProductFragmentToListProduct';

interface UseRingPDPProductsDataProps {
  currentRoute: CurrentRouteValues;
  productsData: ListProduct[] | undefined;
  ringHandlePrefix: string;
}

export interface UseRingPDPProductsDataState {
  oneMonthMembershipSku: string;
  oneYearMembership: ListProduct | undefined;
  oneYearMembershipSku: string;
  product: ListProduct | undefined;
  ringSizesData: RingSizeData[] | undefined;
  subscription: ListProduct | undefined;
  threeYearWarranty: ListProduct | undefined;
  threeYearWarrantySku: string;
  twoYearWarranty: ListProduct | undefined;
  twoYearWarrantySku: string;
}

const useRingPDPProductsData = ({
  currentRoute,
  productsData,
  ringHandlePrefix,
}: UseRingPDPProductsDataProps): UseRingPDPProductsDataState => {
  // Create a dictionary of products by their handle for efficient lookup
  const productsDataByHandle = productsData?.reduce(
    (acc, cur) => ({
      ...acc,
      [cur.handle]: cur,
    }),
    {} as Record<string, ListProduct>,
  );

  const product =
    productsDataByHandle?.[`${ringHandlePrefix}-${currentRoute.finish.value}`];

  const subscription = productsDataByHandle?.['subscription'];

  const oneYearMembership = productsDataByHandle?.['one-year-membership'];

  const twoYearWarranty = productsDataByHandle?.['extended-warranty-2year'];

  const threeYearWarranty = productsDataByHandle?.['extended-warranty-3year'];

  const ringSizesData = useMemo(() => {
    return product?.variants.map((variant) => ({
      size: variant.selectedOptions[0]?.value ?? '',
      sku: variant.sku,
      stockStatus: (variant.inventory?.retail ?? IN_STOCK) as StockStatus,
    }));
  }, [product?.variants]);

  const oneMonthMembershipSku = subscription?.variants[0]?.sku ?? '';
  const oneYearMembershipSku = oneYearMembership?.variants[0]?.sku ?? '';
  const twoYearWarrantySku = twoYearWarranty?.variants[0]?.sku ?? '';
  const threeYearWarrantySku = threeYearWarranty?.variants[0]?.sku ?? '';

  return {
    oneMonthMembershipSku,
    oneYearMembership,
    oneYearMembershipSku,
    product,
    ringSizesData,
    subscription,
    threeYearWarranty,
    threeYearWarrantySku,
    twoYearWarranty,
    twoYearWarrantySku,
  };
};

export default useRingPDPProductsData;
